import {
  AlbumContentType,
  RecordingState,
  SelfRecordingStatus,
} from 'common/enums';
import { selfRecordingActions } from './action-types';

export type VideosRecordedType = {
  requestId: string;
  videoId: number;
  videoUrl: string;
  status: SelfRecordingStatus;
  description?: string;
  name: string;
  isAudioOnly?: boolean;
  imageId?: string;
  file?: File;
  fileName?: string;
  uploadStartTime?: Date;
  videoSignedId: string;
  positionInBatchToClip?: number;
  contentType: AlbumContentType;
};

export type AlbumUploadEntryProps = {
  type: AlbumContentType;
  name?: string;
  data?: string;
  isUploading: boolean;
  uploadId: string;
  error?: boolean;
};

export type UploadMode = 'record' | 'upload' | 'audio';

// The date range and location picked in the Add Entry dialog, given to the
// memory it creates (not to its album).
export type EntryMeta = {
  startDate?: string;
  endDate?: string;
  location?: string;
};

export type InitialStateType = {
  videosRecorded: VideosRecordedType[];
  isRecording: boolean;
  recordingState: RecordingState;
  selectedQuestion?: ClipType;
  vaultId: number;
  albumId: number;
  readyForNextStep: boolean;
  mode?: UploadMode;
  mediaStream?: MediaStream;
  isUploading: boolean;
  batchId?: string;
  clipNumber: number;
  uploads: AlbumUploadEntryProps[];
  uploadResponses: Record<string, any>;
  entryMeta: EntryMeta;
};

const initialState: InitialStateType = {
  videosRecorded: [],
  isRecording: false,
  recordingState: RecordingState.READY_TO_RECORD,
  selectedQuestion: undefined,
  vaultId: 0,
  albumId: 0,
  readyForNextStep: false,
  mediaStream: undefined,
  isUploading: false,
  batchId: undefined,
  clipNumber: 0,
  uploads: [],
  uploadResponses: {},
  entryMeta: {},
};

type InformationToUpdate = {
  requestId: string;
  description: string;
  name: string;
  imageId: string;
  file?: File;
  status?: SelfRecordingStatus;
};

type AudioOnlyType = {
  requestId: string;
  isAudioOnly: boolean;
};

type UpdatePositionInBatch = {
  requestId: string;
  positionInBatch: number;
};

type Action = {
  type: string;
  videoToAppend: VideosRecordedType;
  selectedQuestion: ClipType;
  videoToDelete: string;
  vaultId: number;
  albumId: number;
  updateInformation: InformationToUpdate;
  idWhoseVideoFailed: string;
  mode?: UploadMode;
  mediaStream: MediaStream;
  isRecording: boolean;
  isUploading: boolean;
  updateAudioOnly: AudioOnlyType;
  positionInBatchToClip: UpdatePositionInBatch;
  clipNumber: number;
  addUpload: AlbumUploadEntryProps;
  updateUpload: AlbumUploadEntryProps;
  removeUpload: string;
  uploadResponse: { uploadId: string; response: any };
  recordingState: RecordingState;
  entryMeta: EntryMeta;
};

const SelfRecordingReducer = (state: InitialStateType, action: Action) => {
  switch (action.type) {
    case selfRecordingActions.addVideo:
      return {
        ...state,
        videosRecorded: state.videosRecorded.map((video) => {
          if (video.requestId === action.videoToAppend.requestId) {
            return {
              ...video,
              videoId: action.videoToAppend.videoId,
              videoUrl: action.videoToAppend.videoUrl,
              videoSignedId: action.videoToAppend.videoSignedId,
              status: SelfRecordingStatus.recorded,
            };
          }
          return video;
        }),
      };
    case selfRecordingActions.setIsRecording:
      return {
        ...state,
        isRecording: action.isRecording,
      };
    case selfRecordingActions.setIsUploading:
      return {
        ...state,
        isUploading: action.isUploading,
      };
    case selfRecordingActions.setSelectedQuestion:
      return {
        ...state,
        selectedQuestion: action.selectedQuestion,
      };
    case selfRecordingActions.deleteAllVideos:
      return {
        ...state,
        videosRecorded: [],
        readyForNextStep: false,
      };
    case selfRecordingActions.deleteVideo:
      return {
        ...state,
        videosRecorded: state.videosRecorded.filter(
          (video) => video.requestId !== action.videoToDelete,
        ),
      };
    case selfRecordingActions.setVaultId:
      return {
        ...state,
        vaultId: action.vaultId,
      };
    case selfRecordingActions.setAlbumId:
      return {
        ...state,
        albumId: action.albumId,
      };
    case selfRecordingActions.setEntryMeta:
      return {
        ...state,
        entryMeta: action.entryMeta,
      };
    case selfRecordingActions.newClip:
      return {
        ...state,
        videosRecorded: state.videosRecorded.concat(action.videoToAppend),
      };
    case selfRecordingActions.setReadyForNextStep:
      return {
        ...state,
        readyForNextStep:
          state.videosRecorded.length > 0
            ? state.videosRecorded.every(
                (video) => video.status === SelfRecordingStatus.recorded,
              )
            : false,
      };
    case selfRecordingActions.updateInformation:
      return {
        ...state,
        videosRecorded: state.videosRecorded.map((video) => {
          if (video.requestId === action.updateInformation.requestId) {
            return {
              ...video,
              description: action.updateInformation.description
                ? action.updateInformation.description
                : video.description,
              name: action.updateInformation.name
                ? action.updateInformation.name
                : video.name,
              imageId: action.updateInformation.imageId
                ? action.updateInformation.imageId
                : video.imageId,
              file: action.updateInformation.file
                ? action.updateInformation.file
                : video.file,
              status: action.updateInformation.status
                ? action.updateInformation.status
                : video.status,
            };
          }
          return video;
        }),
      };
    case selfRecordingActions.updateAudioOnly:
      return {
        ...state,
        videosRecorded: state.videosRecorded.map((video) => {
          if (video.requestId === action.updateAudioOnly.requestId) {
            return {
              ...video,
              isAudioOnly: action.updateAudioOnly.isAudioOnly,
            };
          }
          return video;
        }),
      };
    case selfRecordingActions.videoFailed:
      return {
        ...state,
        videosRecorded: state.videosRecorded.map((video) => {
          if (video.requestId === action.idWhoseVideoFailed) {
            return {
              ...video,
              status: SelfRecordingStatus.error,
            };
          }
          return video;
        }),
      };
    case selfRecordingActions.setMode:
      return {
        ...state,
        mode: action.mode,
      };
    case selfRecordingActions.setMediaStream:
      return {
        ...state,
        mediaStream: action.mediaStream,
      };
    case selfRecordingActions.cleanState: {
      state.mediaStream?.getTracks().forEach((track) => {
        track.stop();
      });
      return initialState;
    }
    case selfRecordingActions.setBatchId:
      return {
        ...state,
        batchId: Math.random().toString(36).substr(2, 10),
      };
    case selfRecordingActions.setPositionInBatchToClip:
      return {
        ...state,
        videosRecorded: state.videosRecorded.map((video) => {
          if (video.requestId === action.positionInBatchToClip.requestId) {
            return {
              ...video,
              positionInBatchToClip:
                action.positionInBatchToClip.positionInBatch,
            };
          }
          return video;
        }),
      };
    case selfRecordingActions.setClipNumber:
      return {
        ...state,
        clipNumber: action.clipNumber,
      };
    case selfRecordingActions.addUpload:
      return {
        ...state,
        uploads: [...state.uploads, action.addUpload],
      };
    case selfRecordingActions.updateUpload:
      return {
        ...state,
        uploads: state.uploads.map((upload) => {
          if (upload.uploadId === action.updateUpload.uploadId) {
            return action.updateUpload;
          }
          return upload;
        }),
      };
    case selfRecordingActions.removeUpload:
      return {
        ...state,
        uploads: state.uploads.filter(
          (upload) => upload.uploadId !== action.removeUpload,
        ),
      };
    case selfRecordingActions.setUploadResponse:
      return {
        ...state,
        uploadResponses: {
          ...state.uploadResponses,
          [action.uploadResponse.uploadId]: action.uploadResponse.response,
        },
      };
    case selfRecordingActions.setRecordingState:
      return {
        ...state,
        recordingState: action.recordingState,
      };
    default:
      return state;
  }
};

export { initialState, SelfRecordingReducer };
